import * as React from "react";
import {
  Grid,
  TextField,
  InputLabel,
  MenuItem,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import {} from "@material-ui/pickers";
import GeneralModal from "../../modals/GeneralModal";
import FlashMessage from "../../shared/FlashMessage";
import InputMask from "react-input-mask";
import { getHeaders } from "../../utils/HeaderHelper";
import { AuthenticationContext } from "../../Context";
import { validateEmail } from "../../utils/StringHelper";
import ClearIcon from "@mui/icons-material/Clear";
import { careers } from "../../globals/globals";
import { isInt } from "../../utils/NumberTypeHelper";
import ActivateDeactivateProviderModal from "./ActivateDeactivateProviderModal";
import { useFlash } from "../../FlashContext";

interface Props {
  selectedProvider: any;
  csrfToken: string;
  showAddProviderModal: boolean;
  setShowAddProviderModal: any;
  unsaveChanges: boolean;
  setUnsavedChanges: any;
  setUnsavedModalOpen: any;
  setIsEditing: any;
  setSelectedProvider: any;
  updateProviderReloadKey?: () => void;
}

const AddProviderModal: React.FC<Props> = ({
  selectedProvider,
  csrfToken,
  showAddProviderModal,
  setShowAddProviderModal,
  unsaveChanges,
  setUnsavedChanges,
  setUnsavedModalOpen,
  setIsEditing,
  setSelectedProvider,
  updateProviderReloadKey,
}) => {
  const { showToast } = useFlash();
  const authenticationSetting = React.useContext(AuthenticationContext);
  const currentUser = authenticationSetting.currentUser;

  const [firstName, setFirstName] = React.useState<string>(
    selectedProvider?.first_name ?? ""
  );
  const [middleName, setMiddleName] = React.useState<string>(
    selectedProvider?.middle_name ?? ""
  );
  const [lastName, setLastName] = React.useState<string>(
    selectedProvider?.last_name ?? ""
  );
  const [email, setEmail] = React.useState<string>(
    selectedProvider?.email ?? ""
  );
  const [career, setCareer] = React.useState<string>(
    selectedProvider?.career ?? ""
  );
  const [mobilePhoneNumber, setMobilePhoneNumber] = React.useState<string>(
    selectedProvider?.mobile_phone_number ?? ""
  );
  const [businessPhoneNumber, setBusinessPhoneNumber] = React.useState<string>(
    selectedProvider?.business_phone_number ?? ""
  );
  const [providerNpiNumber, setProviderNpiNumber] = React.useState<string>(
    selectedProvider?.provider_npi_number ?? ""
  );
  const [credentials, setCredentials] = React.useState<string>(
    selectedProvider?.creds ?? ""
  );
  const [idQualifier, setIdQualifier] = React.useState<string>(
    selectedProvider?.id_qualifier ?? ""
  );
  const [renderingProviderId, setRenderingProviderId] = React.useState<string>(
    selectedProvider?.id_number ?? ""
  );

  const [loading, setLoading] = React.useState<boolean>(false);
  const [showDeactivateModal, setShowDeactivateModal] =
    React.useState<boolean>(false);

  const idQualifiersOptions = [
    { value: "0B", label: "0B - State License Number" },
    { value: "1G", label: "1G - Provider UPIN Number" },
    { value: "G2", label: "G2 - Provider Commercial Number" },
    { value: "LU", label: "LU - Location Number" },
    { value: "ZZ", label: "ZZ - Provider Taxonomy" },
  ];

  const openDeactivateProviderModal = () => setShowDeactivateModal(true);
  const closeDeactivateProviderModal = () => setShowDeactivateModal(false);

  const handleModalClose = () => {
    event.preventDefault();
    if (selectedProvider && unsaveChanges) {
      setUnsavedModalOpen(true);
    } else {
      setShowAddProviderModal(false);
      setUnsavedChanges(false);
    }
    setIsEditing(false);
  };

  const validForm = () => {
    if (!firstName.trim()) {
      showToast({
        title: "First Name Invalid",
        text: "First Name is required",
        type: "error",
      });
      return false;
    }

    if (!lastName.trim()) {
      showToast({
        title: "Last Name Invalid",
        text: "Last Name is required",
        type: "error",
      });
      return false;
    }

    if (!email.trim()) {
      showToast({
        title: "Email Invalid",
        text: "Email is required",
        type: "error",
      });
      return false;
    }

    if (!validateEmail(email)) {
      showToast({
        title: "Email Invalid",
        text: "Invalid email format",
        type: "error",
      });
      return false;
    }

    // Validates Rendering Provider NPI: optional, but if provided must be exactly 10 digits.
    if (
      providerNpiNumber &&
      (providerNpiNumber.length !== 10 || !isInt(providerNpiNumber))
    ) {
      showToast({
        title: "Provider NPI Number Invalid",
        text: "Invalid Provider NPI Number. Must be 10 digits.",
        type: "error",
      });
      return false;
    }

    if (renderingProviderId.trim() && !idQualifier) {
      showToast({
        title: "ID Qualifier Required",
        text:
          "ID Qualifier is required when Rendering Provider ID # is provided",
        type: "error",
      });
      return false;
    }

    if (idQualifier && !renderingProviderId.trim()) {
      showToast({
        title: "Rendering Provider ID # Required",
        text:
          "Rendering Provider ID # is required when ID Qualifier is provided",
        type: "error",
      });
      return false;
    }

    return true;
  };

  const inviteProvider = () => {
    if (!validForm()) return;

    setLoading(true);

    fetch(`/user`, {
      method: "POST",
      headers: getHeaders(csrfToken),
      body: JSON.stringify({
        user: {
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          email,
          mobile_phone_number: mobilePhoneNumber,
          business_phone_number: businessPhoneNumber,
          role: "provider",
          career,
          provider_npi_number: providerNpiNumber,
          creds: credentials,
          id_number: renderingProviderId,
          id_qualifier: idQualifier,
        },
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.error) {
          showToast({
            title: "Error",
            text: result.error,
            type: "error",
          });
          setLoading(false);
          return;
        }

        showToast({
          title: "Success",
          text: result.message,
          type: "success",
        });

        setShowAddProviderModal(false);
        setUnsavedChanges(false);
        updateProviderReloadKey();
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        showToast({
          title: "Error",
          text: "Something went wrong.",
          type: "error",
        });
        setLoading(false);
      });
};

  const updateProvider = () => {
    if (!validForm()) return;

  setLoading(true);

  fetch(`user/${selectedProvider?.id}`, {
    method: "PATCH",
    headers: getHeaders(csrfToken),
    body: JSON.stringify({
      user: {
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        email,
        mobile_phone_number: mobilePhoneNumber,
        business_phone_number: businessPhoneNumber,
        role: "provider",
        career,
        provider_npi_number: providerNpiNumber,
        creds: credentials,
        id_number: renderingProviderId,
        id_qualifier: idQualifier,
      },
    }),
  })
    .then((res) => res.json())
    .then((result) => {
      if (result.error) {
        showToast({
          title: "Error",
          text: result.error,
          type: "error",
        });
        setLoading(false);
        return;
      }

      showToast({
        title: "Success",
        text: result.message,
        type: "success",
      });

      setSelectedProvider((prev: any) => ({
        ...prev,
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        email,
        mobile_phone_number: mobilePhoneNumber,
        business_phone_number: businessPhoneNumber,
        role: "provider",
        career,
        provider_npi_number: providerNpiNumber,
        creds: credentials,
        id_number: renderingProviderId,
        id_qualifier: idQualifier,
      }));

      setShowAddProviderModal(false);
      setUnsavedChanges(false);
      updateProviderReloadKey();
      setLoading(false);
    })
    .catch((err) => {
      console.error(err);
      showToast({
        title: "Error",
        text: "Something went wrong.",
        type: "error",
      });
      setLoading(false);
    });
  };

  const sendPasswordResetEmail = async () => {
    setLoading(true);

    try {
      const response = await fetch("/reset_password", {
        method: "POST",
        headers: {
          ...getHeaders(csrfToken),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      });

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Success",
          text: result.message,
          type: "success",
        });
      } else {
        showToast({
          title: "Error",
          text: result.message,
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Error",
        text: "An error occurred while sending password reset email",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderActivateDeactivate = () => {
    if (!selectedProvider) {
      return false;
    }

    const url = `user/${selectedProvider.user_id}/${
      selectedProvider.is_active ? "deactivate" : "reactivate"
    }`;

    fetch(url, {
      method: "PATCH",
      headers: getHeaders(csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          alert(result.error);
        } else {
          closeDeactivateProviderModal();
          setShowAddProviderModal(false);
          setUnsavedChanges(false);
          setSelectedProvider(null);
          updateProviderReloadKey();
          showToast({
            title: "Success",
            text: result.message,
            type: "success",
          });
        }
      })
      .catch((error) => {
        closeDeactivateProviderModal();
        handleModalClose();
        console.log(error);
      });
  };

  const canDeactivateProvider = () => {
    if (!selectedProvider || !currentUser) return false;

    const isSelf = selectedProvider.user_id === currentUser.id;
    const isStarfieldAdmin = selectedProvider.starfield_admin;
    const isOrgAdmin = currentUser.organization_admin;
    const isStarfieldAdminUser = currentUser.starfield_admin;

    // Basic users cannot deactivate anyone
    if (!isOrgAdmin && !isStarfieldAdminUser) return false;

    // Users cannot deactivate themselves
    if (isSelf) return false;

    // Organization Admin rules
    if (isOrgAdmin && !isStarfieldAdminUser) {
      // Org Admins can deactivate only Basic Users and Org Users in their org
      return !isStarfieldAdmin;
    }

    // Starfield Admin rules
    if (isStarfieldAdminUser) {
      // Starfield Admins can deactivate anyone except themselves (already checked)
      return true;
    }

    return false;
  };

  console.log("currentUser", currentUser);
  console.log("selectedProvider", selectedProvider);

  return (
    <GeneralModal
      open={showAddProviderModal}
      title={selectedProvider ? "Edit Provider" : "Add Provider"}
      successCallback={selectedProvider ? updateProvider : inviteProvider}
      closeCallback={handleModalClose}
      containerClassName="add-provider-modal"
      width="675px"
      isLoading={false}
      confirmButtonText={selectedProvider ? "Save" : "Create"}
      cancelButtonText="Cancel"
    >
      <Grid container className="form-container" spacing={1}>
        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel
                htmlFor="firstName"
                className="field-label"
                sx={{
                  marginTop: "0px !important",
                  paddingTop: "0px !important",
                }}
              >
                First Name*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="firstName"
                size="small"
                value={firstName}
                className="the-field"
                required
                variant="outlined"
                onChange={(event) => {
                  setFirstName(event.target.value);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
                slotProps={{
                  htmlInput: { maxLength: 50 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="middleName" className="field-label">
                Middle Name
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="middleName"
                size="small"
                value={middleName}
                className="the-field"
                variant="outlined"
                onChange={(event) => {
                  setMiddleName(event.target.value);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
                slotProps={{
                  htmlInput: { maxLength: 50 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="lastName" className="field-label">
                Last Name*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="lastName"
                size="small"
                value={lastName}
                className="the-field"
                required
                variant="outlined"
                onChange={(event) => {
                  setLastName(event.target.value);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
                slotProps={{
                  htmlInput: { maxLength: 50 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="email" className="field-label">
                Email Address*
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="email"
                size="small"
                value={email}
                required
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                onKeyPress={() => {
                  setUnsavedChanges(true);
                }}
                slotProps={{
                  htmlInput: { maxLength: 256 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="mobilePhoneNumber" className="field-label">
                Mobile Phone Number
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <InputMask
                id="mobilePhoneNumber"
                mask="999-999-9999"
                value={mobilePhoneNumber}
                disabled={false}
                maskChar=""
                variant="outlined"
                className="the-field"
                size="small"
                onChange={(event) => {
                  setMobilePhoneNumber(event.target.value);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
              >
                {() => (
                  <TextField
                    variant="outlined"
                    size="small"
                    className="the-field"
                  />
                )}
              </InputMask>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="businessPhoneNumber" className="field-label">
                Business Phone Number
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <InputMask
                id="businessPhoneNumber"
                mask="999-999-9999"
                value={businessPhoneNumber}
                disabled={false}
                maskChar=""
                variant="outlined"
                className="the-field"
                size="small"
                onChange={(event) => {
                  setBusinessPhoneNumber(event.target.value);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
              >
                {() => (
                  <TextField
                    variant="outlined"
                    size="small"
                    className="the-field"
                  />
                )}
              </InputMask>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={12}>
              <InputLabel
                htmlFor="career"
                className="field-label"
                sx={{
                  marginTop: "0px !important",
                  paddingTop: "0px !important",
                }}
              >
                Career
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="career"
                value={career}
                required
                size="small"
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setCareer(event.target.value);
                  setUnsavedChanges(true);
                }}
                select
                slotProps={{
                  input: {
                    endAdornment: career && (
                      <InputAdornment
                        position="end"
                        sx={{ marginRight: "12px" }}
                      >
                        <ClearIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setCareer("");
                          }}
                          sx={{
                            cursor: "pointer",
                          }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              >
                {careers.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="credentials" className="field-label">
                Credentials
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="credentials"
                size="small"
                value={credentials}
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setCredentials(event.target.value);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
                slotProps={{
                  htmlInput: { maxLength: 50 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="providerNpiNumber" className="field-label">
                Provider NPI #
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="providerNpiNumber"
                size="small"
                value={providerNpiNumber}
                required
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  const npi = event.target.value.replace(/\D/g, "");
                  setProviderNpiNumber(npi);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
                slotProps={{
                  htmlInput: { inputMode: "numeric", maxLength: 10 },
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="idQualifier" className="field-label">
                ID Qualifier
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="idQualifier"
                value={idQualifier}
                required
                size="small"
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setIdQualifier(event.target.value);
                  setUnsavedChanges(true);
                }}
                select
                slotProps={{
                  input: {
                    endAdornment: idQualifier && (
                      <InputAdornment
                        position="end"
                        sx={{ marginRight: "12px" }}
                      >
                        <ClearIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setIdQualifier("");
                          }}
                          sx={{
                            cursor: "pointer",
                          }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              >
                {idQualifiersOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <InputLabel htmlFor="renderingProviderId" className="field-label">
                Rendering Provider ID #
              </InputLabel>
            </Grid>
            <Grid item xs={12} className="field-container">
              <TextField
                id="renderingProviderId"
                size="small"
                value={renderingProviderId}
                variant="outlined"
                className="the-field"
                onChange={(event) => {
                  setRenderingProviderId(event.target.value);
                }}
                onKeyPress={(event) => {
                  setUnsavedChanges(true);
                }}
                slotProps={{ htmlInput: { maxLength: 29 } }}
              />
            </Grid>
          </Grid>

          {selectedProvider && (
            <Grid container>
              <Grid item xs={12}>
                <InputLabel
                  htmlFor="renderingProviderId"
                  className="field-label"
                >
                  Password Reset to Provider
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <Button
                  onClick={sendPasswordResetEmail}
                  className="confirm-btn"
                  sx={{
                    width: "95%",
                    height: "41.13px",
                  }}
                >
                  Send Password Reset Email
                </Button>
              </Grid>
            </Grid>
          )}

          {canDeactivateProvider() && (
            <Grid container>
              <Grid item xs={12}>
                <InputLabel
                  htmlFor="renderingProviderId"
                  className="field-label"
                >
                  {selectedProvider?.is_active
                    ? "Deactivate Provider"
                    : "Reinstate Provider"}
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <Button
                  onClick={openDeactivateProviderModal}
                  className="confirm-btn"
                  sx={{
                    width: "95%",
                    height: "41.13px",
                    backgroundColor: selectedProvider.is_active
                      ? "#FF0000"
                      : "#FF890A",
                    "&:hover": {
                      backgroundColor: selectedProvider.is_active && "#CC0000",
                    },
                  }}
                >
                  {selectedProvider?.is_active
                    ? "Deactivate Provider"
                    : "Reinstate Provider"}
                </Button>
              </Grid>

              {showDeactivateModal && (
                <ActivateDeactivateProviderModal
                  showModal={showDeactivateModal}
                  closeModal={closeDeactivateProviderModal}
                  handleOnClick={handleProviderActivateDeactivate}
                  isActive={selectedProvider.is_active}
                />
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </GeneralModal>
  );
};

export default AddProviderModal;
